import React from 'react';

import i18n from '@client/utils/i18n';
import { SpectralRanges } from '@ledium/types';
import ArticleIcon from '@mui/icons-material/Article';
import { IconButton, Paper, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Typography, { TypographyProps } from '@mui/material/Typography';

import { If } from './If';

interface SpectrogramWithRanges {
    pfd: SpectralRanges;
    irradiance: SpectralRanges;
}

interface DetailedIntensityInfoProps {
    spectrogram: SpectrogramWithRanges;
    labelKey?: string | null;
    label?: React.ReactNode;
    unit?: 'pfd' | 'irradiance';
    typography?: TypographyProps['variant'];
    iconOnly?: boolean;
}

// const spectrumOrder = ['uvA', 'uvB', 'uvC', 'uv', 'blue', 'green', 'red', 'fr', 'ir', 'visibleSum', 'sum'];
const spectrumOrder = ['uv', 'blue', 'green', 'red', 'fr', 'ir', 'visibleSum', 'sum'];

export const DetailedIntensityInfo = ({
    spectrogram,
    labelKey = null,
    label = null,
    unit = 'irradiance',
    typography = 'body2',
    iconOnly = false
}: DetailedIntensityInfoProps) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    return (
        <Typography display="inline" variant={typography}>
            <If condition={!iconOnly}>
                <If condition={!!labelKey}>{i18n(labelKey as string)}</If>
                <If condition={!!label}>{label}</If>
                <b> {spectrogram[unit].sum}</b>
                <span> {i18n(`${unit}_unit`)}</span>
            </If>
            <IconButton size="small" color="primary" onMouseEnter={(e) => setAnchorEl(e.currentTarget)} onMouseLeave={() => setAnchorEl(null)}>
                <ArticleIcon fontSize="inherit" />
            </IconButton>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                sx={{ pointerEvents: 'none' }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ padding: '3px 10px' }}>Range</TableCell>
                                <TableCell sx={{ padding: '3px 10px' }} align="right">
                                    {i18n(unit + '_unit')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {spectrumOrder.map((name) => (
                                <TableRow key={name}>
                                    <TableCell sx={{ padding: '3px 10px' }}>{i18n('detail_' + name)}</TableCell>
                                    <TableCell sx={{ padding: '3px 10px' }} align="right">
                                        {spectrogram[unit][name as keyof SpectralRanges]}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Popover>
        </Typography>
    );
};
